import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useTracker } from '@archipro-website/tracker';
import {
    trackBulkEnquiryItemAdded,
    trackBulkEnquiryItemRemoved,
} from './bulk-enquiry-events';

/** The entity kinds a bulk-enquire tile can represent. */
export type BulkEnquiryItemType = 'product' | 'professional' | 'project';

/** One tile the user has added to the bulk enquiry, keyed by its SiteTree id. */
export interface BulkEnquiryItem {
    /** SiteTree id of the item — becomes an entry in `EnquiryInput.SiteTreeIDs`. */
    siteTreeID: number;
    /**
     * The professional the enquiry is sent to — an entry in
     * `EnquiryInput.TargetProfessionals`. For a professional tile this is the
     * professional itself; for a product/project it is the seller/owner.
     */
    targetProfessionalId: number;
    itemType: BulkEnquiryItemType;
    /** Shown in the selected-items list / modal summary. */
    title: string;
    /** Thumbnail: a product/project photo, or a professional's logo. */
    image?: string;
    /**
     * True when `image` is a professional's logo rather than a photo. Logos must
     * be shown whole on their brand background, not cropped to fill.
     */
    imageIsLogo?: boolean;
    /** Brand background sitting behind a logo. */
    imageBackground?: string;
    /** The professional's name, shown as the row's supplier line. */
    supplierName?: string;
    /**
     * What a professional does ("Manufacturers and Suppliers"). Only set for
     * professional tiles, where the supplier line would just repeat the title.
     */
    category?: string;
}

export const BULK_ENQUIRY_SELECTION_KEY = 'bulk-enquiry-selection';

/**
 * Is there a stored selection the atom has not caught up with yet?
 *
 * The atom syncs from storage on mount, so the first client commit still reads
 * `[]`. Anything that treats an empty list as "the reader unticked everything"
 * has to tell that apart from "not loaded yet" — on a social return those are
 * the same frame, and getting it wrong closes the gate on a send that is about
 * to go out. Reads storage directly because that is the only synchronous
 * answer available on that first commit.
 */
export const hasStoredBulkSelection = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const raw = window.sessionStorage.getItem(BULK_ENQUIRY_SELECTION_KEY);
        if (!raw) return false;
        const parsed: unknown = JSON.parse(raw);

        return Array.isArray(parsed) && parsed.length > 0;
    } catch {
        return false;
    }
};

/**
 * The live bulk-enquiry selection (newest first), persisted per browser tab
 * in sessionStorage so it survives navigating into a detail page and back — and
 * a refresh — but never leaks into a later visit (it clears when the tab closes,
 * on submit, or on exit). SSR-safe: the server renders the empty initial value
 * and the client syncs from storage after hydration.
 */
export const bulkEnquirySelectionAtom = atomWithStorage<BulkEnquiryItem[]>(
    BULK_ENQUIRY_SELECTION_KEY,
    [],
    createJSONStorage<BulkEnquiryItem[]>(() => sessionStorage),
);

/**
 * Whether bulk-enquiry mode is active, persisted alongside the selection so
 * returning to the browse page restores both. Off by default: tiles show the
 * Save heart and no select checkbox. Entering mode swaps the heart for an
 * always-visible select checkbox on every tile; exiting restores the heart and
 * clears the selection.
 */
export const bulkEnquiryModeAtom = atomWithStorage<boolean>(
    'bulk-enquiry-mode',
    false,
    createJSONStorage<boolean>(() => sessionStorage),
);

/**
 * Toggle reducer shared by the tile checkbox and the tile-wide click. New
 * items go to the FRONT: the island's circles render the list left-to-right,
 * so the newest pick lands leftmost and the "+N" overflow holds the oldest.
 */
export const toggleBulkEnquiryItem = (
    prev: BulkEnquiryItem[],
    item: BulkEnquiryItem,
): BulkEnquiryItem[] =>
    prev.some((entry) => entry.siteTreeID === item.siteTreeID)
        ? prev.filter((entry) => entry.siteTreeID !== item.siteTreeID)
        : [item, ...prev];

export interface UseBulkEnquirySelection {
    /** The selected items, newest first. */
    items: BulkEnquiryItem[];
    /** Number of selected items. */
    count: number;
    /** Number of distinct professionals across the selection. */
    supplierCount: number;
    isSelected: (siteTreeID: number) => boolean;
    /** Add the item if absent, remove it if already selected. */
    toggle: (item: BulkEnquiryItem) => void;
    /** Remove a specific item by its SiteTree id (no-op if absent). */
    remove: (siteTreeID: number) => void;
    /** Remove every item. */
    clear: () => void;
}

/** Read/write access to the bulk-enquiry selection. */
export const useBulkEnquirySelection = (): UseBulkEnquirySelection => {
    const [items, setItems] = useAtom(bulkEnquirySelectionAtom);
    const tracker = useTracker();

    const trackedToggle = useTrackedBulkEnquiryToggle();

    const remove = useCallback(
        (siteTreeID: number) => {
            const present = items.some(
                (entry) => entry.siteTreeID === siteTreeID,
            );
            setItems((prev) =>
                prev.some((entry) => entry.siteTreeID === siteTreeID)
                    ? prev.filter((entry) => entry.siteTreeID !== siteTreeID)
                    : prev,
            );
            if (!present) return;
            const next = items.filter(
                (entry) => entry.siteTreeID !== siteTreeID,
            );
            trackBulkEnquiryItemRemoved(tracker, {
                siteTreeID,
                itemCount: next.length,
                supplierCount: new Set(
                    next.map((entry) => entry.targetProfessionalId),
                ).size,
            });
        },
        [items, setItems, tracker],
    );

    const clear = useCallback(() => {
        // Keep the same reference when already empty so consumers don't re-render.
        setItems((prev) => (prev.length === 0 ? prev : []));
    }, [setItems]);

    const isSelected = useCallback(
        (siteTreeID: number) =>
            items.some((entry) => entry.siteTreeID === siteTreeID),
        [items],
    );

    const supplierCount = useMemo(
        () => new Set(items.map((item) => item.targetProfessionalId)).size,
        [items],
    );

    return {
        items,
        count: items.length,
        supplierCount,
        isSelected,
        toggle: trackedToggle,
        remove,
        clear,
    };
};

/** Lightweight read of just the mode flag — used by every tile to swap overlays. */
export const useBulkEnquiryModeActive = (): boolean =>
    useAtomValue(bulkEnquiryModeAtom);

/**
 * Toggle an item and report it, without subscribing to the selection.
 *
 * Every add and remove goes through here so the funnel sees all of them. The
 * grid tiles reach the selection write-only on purpose — subscribing would
 * re-render every tile on every toggle — so this reads the current value from
 * the store imperatively rather than through `useAtomValue`. The returned
 * callback is stable, which is what keeps the tiles out of the re-render path.
 */
export const useTrackedBulkEnquiryToggle = (): ((
    item: BulkEnquiryItem,
) => void) => {
    const store = useStore();
    const tracker = useTracker();

    return useCallback(
        (item: BulkEnquiryItem) => {
            const previous = store.get(bulkEnquirySelectionAtom);
            const wasSelected = previous.some(
                (entry) => entry.siteTreeID === item.siteTreeID,
            );
            const next = toggleBulkEnquiryItem(previous, item);
            store.set(bulkEnquirySelectionAtom, next);

            const totals = {
                itemCount: next.length,
                supplierCount: new Set(
                    next.map((entry) => entry.targetProfessionalId),
                ).size,
            };
            if (wasSelected) {
                trackBulkEnquiryItemRemoved(tracker, {
                    siteTreeID: item.siteTreeID,
                    ...totals,
                });
                return;
            }
            trackBulkEnquiryItemAdded(tracker, { item, ...totals });
        },
        [store, tracker],
    );
};

/** What a bulk submission fans out to, for the events that report it. */
export interface BulkEnquiryFanOut {
    itemCount: number;
    /**
     * Distinct professionals across the selection — the number of `Enquiry`
     * records the backend will create, since a bulk submit sends no
     * `TargetProfessionals` and is grouped by each item's owner.
     */
    supplierCount: number;
}

/**
 * The fan-out of the live selection, for enriching the submit events.
 *
 * One `CreatedEnquiry` is fired per submission however many recipients it has,
 * so without these counts a bulk enquiry to fifty professionals is
 * indistinguishable from a single one.
 */
export const useBulkEnquiryFanOut = (): BulkEnquiryFanOut => {
    const items = useAtomValue(bulkEnquirySelectionAtom);

    return useMemo(
        () => ({
            itemCount: items.length,
            supplierCount: new Set(
                items.map((item) => item.targetProfessionalId),
            ).size,
        }),
        [items],
    );
};

export interface UseBulkEnquiryMode {
    active: boolean;
    /** Turn bulk mode on (reveal checkboxes, hide hearts). */
    enter: () => void;
    /** Turn bulk mode off and clear the current selection. */
    exit: () => void;
}

/**
 * Empty the enquiry list and leave bulk mode.
 *
 * The post-login auto-submit sends a bulk enquiry from the saved session, without
 * the browse page's modal ever remounting — so nothing there gets to run its
 * success path. Without this the island would come back still showing a list that
 * has already been sent, and a second tap would duplicate every enquiry in it.
 */
export const useResetBulkEnquiry = (): (() => void) => {
    const setActive = useSetAtom(bulkEnquiryModeAtom);
    const setItems = useSetAtom(bulkEnquirySelectionAtom);

    return useCallback(() => {
        setActive(false);
        setItems((prev) => (prev.length === 0 ? prev : []));
    }, [setActive, setItems]);
};

/** Enter/exit bulk-enquiry mode. Exiting also clears the selection. */
export const useBulkEnquiryMode = (): UseBulkEnquiryMode => {
    const [active, setActive] = useAtom(bulkEnquiryModeAtom);
    const setItems = useSetAtom(bulkEnquirySelectionAtom);

    const enter = useCallback(() => setActive(true), [setActive]);

    const exit = useCallback(() => {
        setActive(false);
        setItems((prev) => (prev.length === 0 ? prev : []));
    }, [setActive, setItems]);

    return { active, enter, exit };
};
